import {
  Text,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalHeader,
  useDisclosure,
  ModalCloseButton,
  Select,
  Spinner,
} from '@televet/kibble-ui';
import { sortBy } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Tree, { RawNodeDatum } from 'react-d3-tree';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  AutomationRunActionPromptType,
  useFindAutomationsForVisualizerQuery,
  useFindChannelStatusForVisualizerQuery,
  useFindFormTemplatesForVisualizerQuery,
  VisualizerAutomationActionFragment,
  VisualizerAutomationFragment,
} from 'shared/types/graphql';

const AutomationVisualizer = (): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const [automation, setAutomation] = useState<VisualizerAutomationFragment>();
  const [initialAction, setInitialAction] = useState<VisualizerAutomationActionFragment>();
  const [datum, setDatum] = useState<RawNodeDatum>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: automationsData, loading: isLoadingAutomations } = useFindAutomationsForVisualizerQuery({
    variables: { where: { clinicId: { equals: currentClinicId } } },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });
  const { data: formsData, loading: isLoadingForms } = useFindFormTemplatesForVisualizerQuery({
    variables: { where: { clinicId: { equals: currentClinicId } } },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });
  const { data: statusData, loading: isLoadingStatus } = useFindChannelStatusForVisualizerQuery({
    variables: { where: { clinicSetting: { clinic: { id: { equals: currentClinicId } } } } },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const truncatePrompt = (prompt: string): string => {
    if (prompt.length > 30) {
      return prompt.slice(0, 30) + '...';
    }
    return prompt;
  };

  const processAction = useCallback(
    (action: VisualizerAutomationActionFragment): RawNodeDatum => {
      console.log('debug', action.workflowEventSettingId, action.promptType, action);
      const node: RawNodeDatum = {
        name: action.promptType === AutomationRunActionPromptType.Prompt ? 'Text field' : action.promptType,
      };
      // For all types of prompts
      node.attributes = {
        Prompt: truncatePrompt(action.config?.prompt || ''),
      };
      node.children = [];
      if (action.channelStatusChangeId) {
        node.attributes['Status Change'] =
          statusData?.findManyChannelStatus?.find((status) => status.id === action.channelStatusChangeId)?.name || '';
      }
      node.attributes.Prompt = truncatePrompt(action.config.messageBodyTemplate || '');

      switch (action.promptType) {
        case AutomationRunActionPromptType.Buttons:
          node.attributes['Call to action'] = action.config.callToActionBody;
          for (const button of action.config.buttons) {
            const buttonNode: RawNodeDatum = { name: button.body };

            buttonNode.children = [];

            if (button.channelStatusChangeId) {
              buttonNode.attributes = {};
              buttonNode.attributes['Status Change'] =
                statusData?.findManyChannelStatus?.find((status) => status.id === button.channelStatusChangeId)?.name ||
                '';
            }

            if (button.workflowEventTriggerActionNumber || button.nextWorkflowEventTriggerSettingId) {
              const nextAction = getNextAction(
                {
                  workflowEventSettingId:
                    button.nextWorkflowEventTriggerSettingId || action.workflowEventSettingId || '',
                  workflowEventTriggerActionNumber: button.workflowEventTriggerActionNumber || 1,
                },
                automationsData?.findManyWorkflowEventSetting || [],
              );
              // if there is a child automation
              if (button.nextWorkflowEventTriggerSettingId && nextAction) {
                buttonNode.children.push({
                  name: 'Child Automation',
                  attributes: {
                    Name:
                      automationsData?.findManyWorkflowEventSetting?.find(
                        (automation) => automation.id === button.nextWorkflowEventTriggerSettingId,
                      )?.name || '',
                  },
                  children: [processAction(nextAction)],
                });
              } else {
                if (nextAction) {
                  buttonNode.children.push(processAction(nextAction));
                }
              }
            }
            node.children.push(buttonNode);
          }
          break;

        default:
          if (action.promptType === AutomationRunActionPromptType.Form) {
            node.attributes['Form'] =
              formsData?.findManyFormTemplate?.find((form) => form.id === action.config.formTemplateId)?.title || '';
          }
          if (action.promptType === AutomationRunActionPromptType.Payment) {
            node.attributes['Invoice amount'] = `$${(action.config.invoiceAmount || 0) / 100}`;
          }

          // if there is a child automation
          if (action.config.nextWorkflowEventTriggerSettingId) {
            const nextAction = getNextAction(
              {
                workflowEventSettingId: action.config.nextWorkflowEventTriggerSettingId,
                workflowEventTriggerActionNumber: 1,
              },
              automationsData?.findManyWorkflowEventSetting || [],
            );
            node.children.push({
              name: 'Child Automation',
              attributes: {
                Name:
                  automationsData?.findManyWorkflowEventSetting?.find(
                    (automation) => automation.id === action.config.nextWorkflowEventTriggerSettingId,
                  )?.name || '',
              },

              children: [nextAction ? processAction(nextAction) : { name: 'No action' }],
            });
          } else {
            const nextAction = getNextAction(
              {
                workflowEventSettingId: action.workflowEventSettingId || '',
                workflowEventTriggerActionNumber: action.workflowEventTriggerActionNumber || 0,
              },
              automationsData?.findManyWorkflowEventSetting || [],
            );
            if (nextAction) {
              node.children.push(processAction(nextAction));
            }
          }
          break;
      }
      return node as RawNodeDatum;
    },
    [automationsData?.findManyWorkflowEventSetting, formsData?.findManyFormTemplate, statusData?.findManyChannelStatus],
  );

  const getNextAction = (
    {
      workflowEventSettingId,
      workflowEventTriggerActionNumber,
    }: { workflowEventSettingId: string; workflowEventTriggerActionNumber: number },
    automations: VisualizerAutomationFragment[],
  ): VisualizerAutomationActionFragment | undefined => {
    const nextAutomation = automations.find((automation) => automation.id === workflowEventSettingId);
    const nextAction = nextAutomation?.actions.find(
      (nextAction) => nextAction.order === workflowEventTriggerActionNumber,
    );
    return nextAction;
  };

  useEffect(() => {
    const actions = automation?.actions ?? [];
    const initialAction = sortBy(actions, 'order').find((action) => action.id);

    setInitialAction(initialAction);
  }, [automation?.actions]);

  useEffect(() => {
    if (initialAction && formsData?.findManyFormTemplate && statusData?.findManyChannelStatus) {
      const datum = processAction(initialAction);
      setDatum(datum);
    }
  }, [formsData?.findManyFormTemplate, initialAction, processAction, statusData?.findManyChannelStatus]);

  const automations = useMemo(() => {
    return (
      automationsData?.findManyWorkflowEventSetting?.map((automation) => ({
        label: automation.name,
        value: automation.id,
      })) || []
    );
  }, [automationsData?.findManyWorkflowEventSetting]);

  return (
    <>
      <Flex gap="3" mb="5">
        <Text>Automations Visualizer</Text>
        <Button onClick={onOpen} size="xs">
          Open
        </Button>
      </Flex>
      <Modal size="xxl" isOpen={isOpen} onClose={onClose}>
        <ModalHeader>
          Automation Visualizer
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          {isLoadingAutomations || isLoadingForms || isLoadingStatus ? (
            <Flex justifyContent="center" alignItems="center" height="700px">
              <Spinner size="xl" />
            </Flex>
          ) : (
            <>
              <Select
                placeholder="Select Automation"
                value={automation?.id}
                options={automations}
                listProps={{
                  isSearchable: true,
                  onSelect: (selected): void => {
                    setAutomation(
                      automationsData?.findManyWorkflowEventSetting?.find(
                        (automation) => automation.id === selected.value,
                      ),
                    );
                  },
                }}
              />

              <Box width="100%" height="684px" my="4" border="1px solid lightgray" borderRadius="xl">
                {datum && (
                  <Tree
                    data={datum}
                    orientation="vertical"
                    nodeSize={{ x: 400, y: 200 }}
                    separation={{ nonSiblings: 1, siblings: 1 }}
                  />
                )}
              </Box>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default AutomationVisualizer;
